/**
 * InlineCall — phoneCall overlay style (inline_call).
 * Contact card on scene + dialogue box + choice panel. No phone chrome or hang-up.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { splitCharacterDisplayName } from '../systems/ally-trust';
import { getVisibleTextFromInlineTerms } from '../utils/inline-terms';
import {
  extractPhoneBranchContinuations,
  PhoneBranchContinuation,
  resolvePhoneReplyGotoScene,
} from '../systems/phone-branch';
import { PhoneCallMessage, PhoneCallReplyOption, Scene } from '../types';
import { DialogueBox } from './dialogue-box';
import { FormattedInlineText } from './formatted-inline-text';
import styles from './inline-call.module.css';

interface InlineCallProps {
  contactName: string;
  contactRole?: string;
  contactAvatar?: string;
  messages: PhoneCallMessage[];
  messageDelay?: number;
  incomingCall?: boolean;
  replyOptions?: PhoneCallReplyOption[];
  getBranchScene?: (sceneId: string) => Scene | undefined;
  defaultConvergeSceneId?: string;
  onComplete?: (gotoScene?: string) => void;
  keyboardActive?: boolean;
}

function DirectoryIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 4h11a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6z" />
      <path d="M6 4v18" />
      <path d="M3 8h3M3 12h3M3 16h3" />
    </svg>
  );
}

function Waveform({ active }: { active: boolean }) {
  return (
    <div className={`${styles.wave} ${active ? styles.waveActive : styles.waveMuted}`} aria-hidden>
      {[9, 15, 6, 13, 8].map((height, index) => (
        <i key={index} style={{ height: `${height}px` }} />
      ))}
    </div>
  );
}

export const InlineCall = ({
  contactName,
  contactRole,
  contactAvatar,
  messages,
  messageDelay = 3000,
  incomingCall = false,
  replyOptions,
  getBranchScene,
  defaultConvergeSceneId,
  onComplete,
  keyboardActive = true,
}: InlineCallProps) => {
  const [dialogueText, setDialogueText] = useState<string | null>(null);
  const [awaitingAdvance, setAwaitingAdvance] = useState(false);
  const [pendingReplyOptions, setPendingReplyOptions] = useState<PhoneCallReplyOption[] | null>(null);
  const [waveActive, setWaveActive] = useState(false);
  const [lineTypingEnabled, setLineTypingEnabled] = useState(true);

  const messageIndexRef = useRef(0);
  const rootRepliesOfferedRef = useRef(false);
  const inReplyFlowRef = useRef(false);
  const afterDialogueRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompleteRef = useRef(onComplete);
  const replyOptionsRef = useRef(replyOptions);
  const getBranchSceneRef = useRef(getBranchScene);
  const defaultConvergeSceneIdRef = useRef(defaultConvergeSceneId);
  const playBranchContinuationsRef = useRef<
    (lines: PhoneBranchContinuation[], index: number, onDone: (gotoScene?: string) => void) => void
  >(() => {});

  onCompleteRef.current = onComplete;
  replyOptionsRef.current = replyOptions;
  getBranchSceneRef.current = getBranchScene;
  defaultConvergeSceneIdRef.current = defaultConvergeSceneId;

  const { givenName, role: parsedRole } = splitCharacterDisplayName(contactName);
  const displayRole = contactRole?.trim() || parsedRole;
  const contactInitial = givenName.charAt(0).toUpperCase();

  const finishCall = useCallback((gotoScene?: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setPendingReplyOptions(null);
    setDialogueText(null);
    setAwaitingAdvance(false);
    setWaveActive(false);
    afterDialogueRef.current = null;
    onCompleteRef.current?.(gotoScene);
  }, []);

  const showContactLine = useCallback((text: string, onAfterClick?: () => void) => {
    setLineTypingEnabled(true);
    setDialogueText(text);
    setWaveActive(true);
    afterDialogueRef.current = onAfterClick ?? null;
    setAwaitingAdvance(Boolean(onAfterClick));
  }, []);

  playBranchContinuationsRef.current = (lines, index, onDone) => {
    if (index >= lines.length) {
      onDone(lines.length > 0 ? lines[lines.length - 1]?.gotoScene : undefined);
      return;
    }
    const line = lines[index]!;
    const text = line.contactReply ?? line.playerText;
    showContactLine(text, () => {
      if (index + 1 < lines.length) {
        playBranchContinuationsRef.current(lines, index + 1, onDone);
      } else {
        onDone(line.gotoScene);
      }
    });
  };

  const offerRootReplies = useCallback(() => {
    const options = replyOptionsRef.current;
    if (options?.length) {
      rootRepliesOfferedRef.current = true;
      setLineTypingEnabled(false);
      setPendingReplyOptions(options);
      setWaveActive(false);
      setAwaitingAdvance(false);
      afterDialogueRef.current = null;
      return true;
    }
    return false;
  }, []);

  const advancePreamble = useCallback(() => {
    let idx = messageIndexRef.current;
    while (idx < messages.length && messages[idx]?.sender === 'player') {
      idx += 1;
    }
    if (idx < messages.length) {
      const msg = messages[idx]!;
      messageIndexRef.current = idx + 1;
      showContactLine(msg.text, advancePreamble);
      return;
    }
    if (inReplyFlowRef.current) {
      finishCall();
      return;
    }
    if (!rootRepliesOfferedRef.current && offerRootReplies()) {
      return;
    }
    finishCall();
  }, [messages, showContactLine, offerRootReplies, finishCall]);

  const proceedFromReply = useCallback(
    (option: PhoneCallReplyOption) => {
      if (option.replyOptions?.length) {
        setLineTypingEnabled(false);
        setPendingReplyOptions(option.replyOptions);
        setWaveActive(false);
        setAwaitingAdvance(false);
        afterDialogueRef.current = null;
        return;
      }
      const gotoScene = resolvePhoneReplyGotoScene(
        option,
        defaultConvergeSceneIdRef.current
      );
      if (gotoScene) {
        const branchLines = extractPhoneBranchContinuations(
          getBranchSceneRef.current?.(gotoScene)
        );
        if (branchLines.length > 0) {
          playBranchContinuationsRef.current(branchLines, 0, (goto) => {
            finishCall(goto);
          });
          return;
        }
        finishCall(gotoScene);
        return;
      }
      finishCall();
    },
    [finishCall]
  );

  const handleReplyChoice = useCallback(
    (index: number) => {
      const options = pendingReplyOptions;
      if (!options?.[index]) return;
      const option = options[index]!;
      inReplyFlowRef.current = true;

      flushSync(() => {
        setLineTypingEnabled(false);
        setPendingReplyOptions(null);
        if (option.contactReply) {
          setDialogueText(option.contactReply);
          setWaveActive(true);
          afterDialogueRef.current = () => proceedFromReply(option);
          setAwaitingAdvance(true);
        } else {
          setDialogueText(null);
          setAwaitingAdvance(false);
          setWaveActive(false);
          afterDialogueRef.current = null;
        }
      });

      if (!option.contactReply) {
        proceedFromReply(option);
      }
    },
    [pendingReplyOptions, proceedFromReply]
  );

  const handleDialogueClick = useCallback(() => {
    if (pendingReplyOptions || !awaitingAdvance) return;
    const next = afterDialogueRef.current;
    afterDialogueRef.current = null;
    flushSync(() => {
      setWaveActive(false);
      setAwaitingAdvance(false);
      next?.();
    });
  }, [pendingReplyOptions, awaitingAdvance]);

  useEffect(() => {
    messageIndexRef.current = 0;
    rootRepliesOfferedRef.current = false;
    inReplyFlowRef.current = false;
    afterDialogueRef.current = null;
    if (messages.length === 0) {
      if (offerRootReplies()) return;
      finishCall();
      return;
    }
    advancePreamble();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [messages, advancePreamble, offerRootReplies, finishCall]);

  const showingChoices = Boolean(pendingReplyOptions?.length);
  const tileCount = Math.min(pendingReplyOptions?.length ?? 1, 3);

  return (
    <div className={styles.overlay} aria-live="polite">
      <div className={styles.veil} aria-hidden />

      <div className={`${styles.card} cn-zoom-ui`}>
        <span className={styles.cardLabel}>
          {incomingCall ? 'APPEL ENTRANT' : 'EN APPEL'}
        </span>
        <div className={styles.avatar}>
          {contactAvatar ? (
            <img src={contactAvatar} alt="" />
          ) : (
            <span className={styles.avatarFallback}>{contactInitial}</span>
          )}
        </div>
        <div className={styles.name}>{givenName}</div>
        {displayRole && (
          <div className={styles.role}>
            <DirectoryIcon />
            {displayRole}
          </div>
        )}
        <Waveform active={waveActive} />
      </div>

      {(dialogueText !== null || showingChoices) && (
        <div className={styles.bottom}>
          {dialogueText !== null && (
            <DialogueBox
              speaker={givenName}
              text={dialogueText}
              onClick={handleDialogueClick}
              showContinue={awaitingAdvance && !showingChoices}
              keyboardActive={keyboardActive && awaitingAdvance && !showingChoices}
              enableTyping={lineTypingEnabled}
              className={showingChoices ? styles.embeddedDialogue : undefined}
            />
          )}
          {showingChoices && (
            <div
              className={styles.tiles}
              role="group"
              aria-label="Choix de réponse"
              style={{ '--tile-count': tileCount } as CSSProperties}
            >
              {pendingReplyOptions!.map((option, index) => (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  className={styles.tile}
                  onClick={() => handleReplyChoice(index)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleReplyChoice(index);
                    }
                  }}
                  aria-label={`Répondre : ${getVisibleTextFromInlineTerms(option.text)}`}
                >
                  <FormattedInlineText text={option.text} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
